import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { todayShamsi } from '../../utils/dateHelpers';
import { MdCalendarToday, MdHistory, MdReceiptLong } from 'react-icons/md';
import {
  MdDashboard, MdFolderOpen, MdVideoLibrary, MdPeople,
  MdEvent, MdAttachMoney, MdCheckBox, MdInventory,
  MdMessage, MdBarChart, MdNotifications, MdSettings,
  MdLogout, MdMenu, MdClose, MdMovie, MdSchool, MdCampaign
} from '../../utils/icons';
import { FaMoneyBillWave, FaBoxes } from 'react-icons/fa';


// هر آیتم منو یک رنگ اختصاصی برای آیکنش دارد تا سایدبار رنگی و شاداب دیده شود
const ICON_COLORS = {
  blue: 'bg-blue-100 text-blue-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  pink: 'bg-pink-100 text-pink-600',
  teal: 'bg-teal-100 text-teal-600',
  orange: 'bg-orange-100 text-orange-600',
  green: 'bg-green-100 text-green-600',
  cyan: 'bg-cyan-100 text-cyan-600',
  violet: 'bg-violet-100 text-violet-600',
  amber: 'bg-amber-100 text-amber-600',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-600',
  lime: 'bg-lime-100 text-lime-700',
  rose: 'bg-rose-100 text-rose-600',
  sky: 'bg-sky-100 text-sky-600',
  purple: 'bg-purple-100 text-purple-600',
  red: 'bg-red-100 text-red-600',
  slate: 'bg-slate-100 text-slate-600',
  emerald: 'bg-emerald-100 text-emerald-600',
};

const navItems = [
  { path: '/', icon: <MdDashboard size={18} />, color: 'blue', label: 'داشبورد', roles: ['admin', 'manager'] },
  { path: '/projects', icon: <MdFolderOpen size={18} />, color: 'indigo', label: 'پروژه‌ها', roles: ['admin', 'manager', 'editor'] },
  { path: '/videos', icon: <MdVideoLibrary size={18} />, color: 'pink', label: 'ویدیوها', roles: ['admin', 'manager', 'editor'] },
  { path: '/employees', icon: <MdPeople size={18} />, color: 'teal', label: 'کارمندان', roles: ['admin', 'manager'] },
  { path: '/events', icon: <MdEvent size={18} />, color: 'orange', label: 'رویدادها', roles: ['admin', 'manager'] },
  { path: '/finance', icon: <MdAttachMoney size={18} />, color: 'green', label: 'مالی', roles: ['admin', 'manager'] },
  { path: '/office-debts', icon: <MdReceiptLong size={18} />, color: 'emerald', label: 'قرض دفتر', roles: ['admin', 'manager'] },
  { path: '/tasks', icon: <MdCheckBox size={18} />, color: 'cyan', label: 'تسک‌ ها', roles: ['admin', 'manager', 'editor'] },
  { path: '/equipment', icon: <MdInventory size={18} />, color: 'violet', label: 'تجهیزات', roles: ['admin', 'manager'] },
  { path: '/classes', icon: <MdSchool size={18} />, color: 'amber', label: 'کلاس‌ ها', roles: ['admin', 'manager'] },
  { path: '/sponsership', icon: <MdCampaign size={18} />, color: 'fuchsia', label: 'اسپانسرشیپ', roles: ['admin', 'manager'] },
  { path: '/rental-equipment', icon: <FaBoxes size={16} />, color: 'lime', label: 'تجهیزات کرایی', roles: ['admin', 'manager'] },
  { path: '/daily-expenses', icon: <FaMoneyBillWave size={16} />, color: 'rose', label: 'مصارفات', roles: ['admin', 'manager', 'editor'] },
  { path: '/reports', icon: <MdBarChart size={18} />, color: 'sky', label: 'راپورها', roles: ['admin', 'manager'] },
  { path: '/communications', icon: <MdMessage size={18} />, color: 'purple', label: 'ارتباط', roles: ['admin', 'manager', 'editor'] },
  { path: '/alerts', icon: <MdNotifications size={18} />, color: 'red', label: 'هشدارها', roles: ['admin', 'manager', 'editor'] },
  { path: '/activity-logs', icon: <MdHistory size={18} />, color: 'slate', label: 'گزارش فعالیت', roles: ['admin'] },
];

export default function Sidebar({ alertCount = 0 }) {
  const { user, logout, isAdmin } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const allowed = navItems.filter(item => item.roles.includes(user?.role));

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-5 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center glow-blue">
            <MdMovie size={22} className="text-white" />
          </div>
          <div>
            <div className="font-bold text-gray-800 text-sm"> ویدا پرودکشن</div>
            <div className="text-gray-500 text-xs">Vida Production</div>
          </div>
        </div>
        <div className="mt-3 text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-1.5 flex items-center gap-1.5">
          <MdCalendarToday size={12} className="text-blue-500" />
          <span>{todayShamsi()}</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {allowed.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            end={item.path === '/'}
            className={({ isActive }) =>
              `flex items-center gap-3 px-2.5 py-2 rounded-xl text-sm font-medium transition-all relative group
              ${isActive
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : 'text-gray-600 border border-transparent hover:bg-gray-50 hover:text-gray-800'
              }`
            }
          >
            <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${ICON_COLORS[item.color]}`}>
              {item.icon}
            </span>
            <span>{item.label}</span>
            {item.path === '/alerts' && alertCount > 0 && (
              <span className="mr-auto bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {alertCount > 9 ? '9+' : alertCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div className="p-3 border-t border-gray-200">
        {isAdmin && (
          <NavLink to="/settings" onClick={() => setMobileOpen(false)}
            className="flex items-center gap-3 px-2.5 py-2 rounded-xl text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-800 transition-all mb-1">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-600">
              <MdSettings size={17} />
            </span>
            <span>تنظیمات</span>
          </NavLink>
        )}
        <div className="flex items-center gap-3 px-3 py-2.5">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
            {user?.name?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-gray-800 text-sm font-medium truncate">{user?.name}</div>
            <div className="text-gray-600 text-xs">{user?.role === 'admin' ? 'ادمین' : user?.role === 'manager' ? 'مدیر' : 'ادیتور'}</div>
          </div>
          <button onClick={logout} className="text-red-500 hover:text-red-600 transition-colors p-1 rounded-lg hover:bg-red-100">
            <MdLogout size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex fixed top-0 right-0 h-screen w-64 bg-white shadow-sm border-l border-gray-200 flex-col z-30">
        <SidebarContent />
      </div>

      {/* Mobile Top Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white shadow-sm border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setMobileOpen(true)} className="text-gray-800 p-1">
          <MdMenu size={24} />
        </button>
        <div className="flex items-center gap-2">
          <MdMovie size={20} className="text-blue-600" />
          <span className="text-gray-800 font-bold text-sm">پرودکشن</span>
        </div>
        <NavLink to="/alerts" className="relative">
          <MdNotifications size={22} className={alertCount > 0 ? 'text-red-500' : 'text-gray-600'} />
          {alertCount > 0 && <span className="absolute -top-1 -left-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">{alertCount > 9 ? '9+' : alertCount}</span>}
        </NavLink>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute top-0 right-0 h-full w-72 bg-white shadow-sm border-l border-gray-200 sidebar-transition">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <span className="text-gray-800 font-bold">منو</span>
              <button onClick={() => setMobileOpen(false)} className="text-gray-500 hover:text-gray-800"><MdClose size={22} /></button>
            </div>
            <SidebarContent />
          </div>
        </div>
      )}
    </>
  );
}
